import * as Sentry from "@sentry/react";
import { authService } from "@/services/auth/authService";
import { eventBus } from "../event/eventBus";
import i18n from "@/i18n/config";
export class middleware {
  private baseURL: string;
  private isRefreshing = false;
  private refreshAccessTokenPromise: Promise<string> | null = null;
  private MAX_RETRIES = 3;
  private RETRY_DELAY = 300;

  constructor(baseURL: string) {
    this.baseURL = baseURL;
  }

  async fetch(url: string, options: RequestInit = {}): Promise<Response> {
    // is the URL a full URL or a path?
    const fullUrl = url.startsWith("http") ? url : `${this.baseURL}${url}`;
    const requestWithAuth = await this.addTokenToRequest(options);
    return this.executeRequestWithRetries(fullUrl, requestWithAuth);
  }

  private async executeRequestWithRetries(
    url: string,
    options: RequestInit,
    retryCount = 0,
  ): Promise<Response> {
    try {
      // Execute the request
      const response = await fetch(url, options);

      // If successful or not a 401, return immediately
      if (response.ok || response.status !== 401) {
        return response;
      }

      // If there's no refresh token, the user is unauthenticated — don't attempt refresh
      if (!authService.getRefreshToken()) {
        return response;
      }

      // If we've reached max retries, give up
      if (retryCount >= this.MAX_RETRIES) {
        // burda değil api katmanında eventBus çalıştırıcaz
        eventBus.emit("notify", {
          message: i18n.t("api.sessionExpired"),
          type: "error",
        });
        return response;
      }

      // Handle token refresh and retry
      const newOptions = await this.handleTokenRefresh(options);

      // Calculate exponential backoff delay
      const delay = this.RETRY_DELAY * Math.pow(2, retryCount);

      // Wait before retrying to allow token propagation
      await new Promise((resolve) => setTimeout(resolve, delay));

      // Retry the request with the new token
      return this.executeRequestWithRetries(url, newOptions, retryCount + 1);
    } catch (error) {
      if (
        !(error instanceof Error) ||
        !error.message.includes("Authentication failed")
      ) {
        Sentry.captureException(error, {
          tags: { errorType: "network", component: "middleware" },
        });
      }
      throw error;
    }
  }

  private async addTokenToRequest(options: RequestInit): Promise<RequestInit> {
    const headers = new Headers(options.headers || {});

    const accessToken = authService.getAccessToken();
    if (accessToken) {
      headers.set("Authorization", `Bearer ${accessToken}`);
    }

    return { ...options, headers };
  }

  /**
   * Handle token refresh and prepare for request retry
   */
  private async handleTokenRefresh(
    originalOptions: RequestInit,
  ): Promise<RequestInit> {
    // If already refreshing, wait for that to complete
    if (this.isRefreshing) {
      if (this.refreshAccessTokenPromise) {
        try {
          const newToken = await this.refreshAccessTokenPromise;
          return this.createRequestWithToken(originalOptions, newToken);
        } catch (error) {
          Sentry.captureException(error, {
            tags: { errorType: "auth", component: "middleware" },
            extra: { context: "waiting for in-progress token refresh" },
          });
          this.onAuthenticationFailure();
          throw error;
        }
      }
    }

    try {
      this.isRefreshing = true;
      this.refreshAccessTokenPromise = this.refreshToken();

      const newToken = await this.refreshAccessTokenPromise;
      return this.createRequestWithToken(originalOptions, newToken);
    } catch (error) {
      Sentry.captureException(error, {
        tags: { errorType: "auth", component: "middleware" },
        extra: { context: "token refresh initiation" },
      });
      this.onAuthenticationFailure();
      throw new Error("Authentication failed. Unable to refresh token.");
    } finally {
      this.isRefreshing = false;
      this.refreshAccessTokenPromise = null;
    }
  }
  /**
   * Create a new request with the provided token
   */
  private createRequestWithToken(
    options: RequestInit,
    token: string,
  ): RequestInit {
    const headers = new Headers(options.headers || {});
    headers.set("Authorization", `Bearer ${token}`);

    return { ...options, headers };
  }

  /**
   * Refresh the authentication token
   */
  private async refreshToken(): Promise<string> {
    const refreshToken = authService.getRefreshToken();

    if (!refreshToken) {
      throw new Error("No refresh token available");
    }

    try {
      const response = await fetch(
        `${this.baseURL}/api/v1/web/authentication/refresh-token`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            RefreshToken: refreshToken,
          }),
        },
      );

      if (!response.ok) {
        throw new Error("Token refresh failed");
      }
      const result = await response.json();
      // json() method returns a promise that resolves with the result of parsing the body text as JSON.
      if (result.data && result.data.accessToken && result.data.refreshToken) {
        authService.updateAccessToken(result.data.accessToken);
        authService.updateRefreshToken(result.data.refreshToken);
        return result.data.accessToken;
      } else {
        throw new Error("Invalid token response format");
      }
    } catch (error) {
      this.onAuthenticationFailure();
      throw error;
    }
  }
  private onAuthenticationFailure(): void {
    authService.clearTokens();
    eventBus.emit("notify", {
      message: i18n.t("api.pleaseLogin"),
      type: "error",
    });
    window.location.href = "/login";
  }
}
